'use client'
import { Media } from '@/payload-types'
import { useState, useRef } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Pagination } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/pagination'
import { X } from 'lucide-react'
import PopinPortal from '@/lib/react/PopinPortal'

function ImageGalleryModal({ images, initialIndex = 0, closeAction }: { images: { image: Media }[], initialIndex?: number, closeAction: () => void }) {
	const [selectedImageIndex, setSelectedImageIndex] = useState(initialIndex)
	const overlay = useRef<HTMLDivElement>(null)

	const handleClose = (e: React.MouseEvent<HTMLDivElement>) => {
		if (e.target === overlay.current) {
			closeAction()
		}
	}

	return (
		<PopinPortal>
			<div 
				ref={overlay} 
				onClick={handleClose} 
				className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4"
			>
				<div 
					className="bg-white relative w-full max-w-5xl max-h-[90vh] flex flex-col rounded-lg overflow-hidden"
					onClick={(e) => e.stopPropagation()}
				>
					<button 
						className="absolute top-4 right-4 z-10 bg-white hover:bg-gray-100 rounded-full p-2 text-black cursor-pointer transition-colors shadow-lg"
						onClick={closeAction}
					>
						<X className="size-5" />
					</button>
					
					{/* Main image */}
					<div className="w-full aspect-square bg-slate-200 overflow-hidden">
						<img 
							src={images[selectedImageIndex]?.image.s3Url} 
							alt={images[selectedImageIndex]?.image.alt} 
							className='w-full h-full object-contain' 
						/>
					</div>

					{/* Thumbnails */}
					<div className="w-full overflow-x-auto p-4 bg-white border-t border-gray-200">
						<div className="flex gap-2 justify-center">
							{images.map((item, index) => (
								<div 
									key={index}
									className={`relative w-20 h-20 flex-shrink-0 bg-slate-200 rounded-lg overflow-hidden cursor-pointer border-2 transition-all ${
										index === selectedImageIndex ? 'border-primary-700' : 'border-transparent'
									}`}
									onClick={() => setSelectedImageIndex(index)}
								>
									<img 
										src={item?.image.s3Url} 
										alt={item?.image.alt} 
										className='w-full h-full object-cover' 
									/>
									{index === selectedImageIndex && (
										<div className="absolute inset-0 bg-black/20"></div>
									)}
								</div>
							))}
						</div>
					</div>
				</div>
			</div>
		</PopinPortal>
	)
}

export default function ImageViewer({ images }: { images: { image: Media }[] }) {
	const [currentImage, setCurrentImage] = useState(images[0]);
	const [activeIndex, setActiveIndex] = useState(0);
	const [isGalleryOpen, setIsGalleryOpen] = useState(false);

	const openImageGallery = () => {
		// Find the index of the currently displayed image
		const currentIndex = images.findIndex(img => img.image.id === currentImage?.image.id)
		setIsGalleryOpen(true)
	}

	return (
		<>
			<div className="w-full flex flex-col-reverse gap-2 mb-4">
				{/* Desktop: Thumbnail grid - only show if more than one image */}
				{images.length > 1 && (
					<div className="hidden md:grid w-full grid-cols-3 gap-2">
						{images.slice(0, images.length > 3 ? 2 : 3).map((item, index) => (
							<div 
								key={index} 
								className="relative w-full aspect-square bg-slate-200 rounded-lg overflow-hidden cursor-pointer group"
								onClick={() => setCurrentImage(item)}
							>
								<img src={item?.image.s3Url} alt={item?.image.alt} className='w-full h-full object-cover' />
								<div className="absolute inset-0 bg-gray-500 opacity-50 group-hover:opacity-0 transition-opacity duration-300"></div>
							</div>
						))}
						{ images.length > 3 && (
							<div  
								className="relative w-full aspect-square bg-slate-200 rounded-lg overflow-hidden cursor-pointer group"
								onClick={openImageGallery}
							>
								<img src={images[2]?.image.s3Url} alt={images[2]?.image.alt} className='w-full h-full object-cover' />
								<div className="absolute inset-0 bg-gray-500 opacity-70 group-hover:opacity-80 transition-opacity duration-300 flex items-center justify-center">
									<span className='text-white font-bold text-4xl'>
										+{ images.length - 2 }
									</span>
								</div>
							</div>
						)}
					</div>
				)}

				{/* Mobile: Slider - only show if more than one image */}
				{images.length > 1 ? (
					<div className="md:hidden w-full relative">
						<Swiper
							modules={[Pagination]}
							spaceBetween={0}
							slidesPerView={1}
							pagination={{ clickable: true }}
							className="w-full rounded-lg overflow-hidden"
							onSlideChange={(swiper) => {
								setCurrentImage(images[swiper.activeIndex])
								setActiveIndex(swiper.activeIndex)
							}}
							onInit={(swiper) => setActiveIndex(swiper.activeIndex)}
						>
							{images.map((item, index) => (
								<SwiperSlide key={index}>
									<div className="relative w-full aspect-square bg-slate-200 rounded-lg overflow-hidden">
										<img 
											src={item?.image.s3Url} 
											alt={item?.image.alt} 
											className='w-full h-full object-cover' 
										/>
										{/* Image counter overlay */}
										<div className="absolute top-4 right-4 bg-black/60 text-white px-3 py-1 rounded-full text-sm font-medium">
											{index + 1}/{images.length}
										</div>
									</div>
								</SwiperSlide>
							))}
						</Swiper>
					</div>
				) : (
					/* Mobile: Single image without slider */
					<div className="md:hidden w-full">
						<div className="relative w-full aspect-square bg-slate-200 rounded-lg overflow-hidden">
							<img 
								src={images[0]?.image.s3Url} 
								alt={images[0]?.image.alt} 
								className='w-full h-full object-cover' 
							/>
						</div>
					</div>
				)}

				{/* Desktop: Main image */}
				<div className="hidden md:block flex-1 bg-slate-200 rounded-lg overflow-hidden">
					{currentImage?.image.s3Url && (
						<img src={currentImage?.image.s3Url} alt={currentImage?.image.alt} className='w-full h-full object-cover' />
					)}
				</div>
			</div>

			{/* Image Gallery Modal */}
			{isGalleryOpen && (
				<ImageGalleryModal 
					images={images} 
					initialIndex={images.findIndex(img => img.image.id === currentImage?.image.id)} 
					closeAction={() => setIsGalleryOpen(false)} 
				/>
			)}
		</>
	)
}
